import { CommonModule } from '@angular/common';
import { ChangeDetectorRef, Component, ViewChild, ViewEncapsulation, output } from '@angular/core';
import { DomSanitizer, SafeHtml } from '@angular/platform-browser';
import { TranslateService } from '@ngx-translate/core';
import { VaultBaseComponent } from '@src/app/shared/components/base/vault-base.component';
import { DialogComponent } from '@src/app/shared/components/dialog/dialog.component';
import { RichTextVariable } from '@src/app/shared/components/rich-text-editor/rich-text-editor.models';
import { SharedModule } from '@src/app/shared/shared.module';
import {
  describeUnfilledVariables,
  highlightUnfilledVariables,
  stripUnsafeHtml,
} from '../../../../utils/partner-contract.utils';

/** O que a pré-visualização mostra. */
export interface PartnerContractPreviewData {
  title: string;
  bodyHtml: string;
  unfilledKeys: string[];
  variables: RichTextVariable[];
  /**
   * Revisão do histórico sendo vista (MG4). Ausente = texto atual do editor. Com ela, o texto de
   * ajuda muda e, com `canUseText`, aparece o botão "Usar este texto".
   */
  revision?: { label: string; canUseText: boolean };
}

/**
 * Pré-visualização da minuta (card 3462): o texto **atual do editor** (mesmo sem salvar) numa
 * folha, com as variáveis sem valor destacadas em amarelo, como no PDF. A capa navy da Legatus só
 * existe no PDF.
 *
 * O HTML passa por `stripUnsafeHtml` e depois por `bypassSecurityTrustHtml`: o sanitizador padrão
 * do Angular apagaria as cores (`style`) do texto. O conteúdo vem do próprio editor ou da API, que
 * sanitiza ao gravar.
 */
@Component({
  selector: 'app-modal-partner-contract-preview',
  standalone: true,
  templateUrl: './modal-partner-contract-preview.component.html',
  styleUrls: ['./modal-partner-contract-preview.component.scss'],
  imports: [CommonModule, SharedModule],
  // O HTML injetado não recebe os atributos do encapsulamento; seletores prefixados por `.contract-preview`.
  encapsulation: ViewEncapsulation.None,
})
export class ModalPartnerContractPreviewComponent extends VaultBaseComponent {
  @ViewChild('dialog') dialogRef: DialogComponent | null = null;

  title = '';
  html: SafeHtml = '';
  unfilledDescription = '';
  /** Rótulo da revisão vista ("Revisão 3 · Editado · 16/10/2026 14:02"), ou vazio. */
  revisionLabel = '';
  canUseText = false;

  /** "Usar este texto": o editor carrega título e corpo da revisão (nada é gravado sozinho). */
  readonly useText = output<void>();

  constructor(
    translateService: TranslateService,
    private readonly sanitizer: DomSanitizer,
    private readonly cdr: ChangeDetectorRef
  ) {
    super(translateService);
  }

  open(data: PartnerContractPreviewData): void {
    this.title = data.title;
    this.revisionLabel = data.revision?.label ?? '';
    this.canUseText = !!data.revision?.canUseText;
    this.unfilledDescription = describeUnfilledVariables(data.unfilledKeys, data.variables);
    const safe = stripUnsafeHtml(highlightUnfilledVariables(data.bodyHtml, data.variables));
    this.html = this.sanitizer.bypassSecurityTrustHtml(safe);
    this.cdr.markForCheck();
    this.dialogRef?.open('', '', '', 'Fechar', '', 'partner-contract-preview', false, true);
  }

  isFormValid(): boolean {
    return true;
  }

  submitAction(): void {
    this.dialogRef?.close();
  }

  useThisText(): void {
    this.dialogRef?.close();
    this.useText.emit();
  }
}
